import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type {
  ProxyVaultConnection,
  ProxyVaultExchange,
  ProxyVaultExchangeDetail,
  ProxyVaultLedgerEntry,
  ProxyVaultOverview,
  ProxyVaultPreview,
  ProxyVaultRevision,
} from '../lib/api-client';

export type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Every hook here is deliberately thin. The Proxy Vault's rules — which
 * connection wins, whether a value may be shared, whether an edit widens a
 * route — all live on the server, and re-deriving any of them here would be a
 * second implementation of a security rule that can drift from the first.
 * The console asks and renders; it does not decide.
 */

export function useProxyVaultOverview(): {
  overview: ProxyVaultOverview | null;
  status: LoadStatus;
  reload: () => void;
} {
  const api = useApiClient();
  const [overview, setOverview] = useState<ProxyVaultOverview | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    api
      .getProxyVaultOverview()
      .then((data) => {
        if (cancelled) return;
        setOverview(data);
        setStatus('success');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [api, nonce]);

  return { overview, status, reload: () => setNonce((n) => n + 1) };
}

export interface ConnectionFilters {
  search: string;
  status: 'all' | 'active' | 'incomplete' | 'revoked';
  tenantId: string | null;
}

export function useProxyVaultConnections(): {
  connections: ProxyVaultConnection[];
  status: LoadStatus;
  error: string | null;
  filters: ConnectionFilters;
  setFilters: (f: ConnectionFilters) => void;
  reload: () => void;
} {
  const api = useApiClient();
  const [connections, setConnections] = useState<ProxyVaultConnection[]>([]);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [filters, setFilters] = useState<ConnectionFilters>({
    search: '',
    status: 'all',
    tenantId: null,
  });

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    const query: Record<string, string> = { include_revoked: 'true' };
    if (filters.search) query['search'] = filters.search;
    if (filters.status !== 'all') query['status'] = filters.status;
    if (filters.tenantId) query['tenant_id'] = filters.tenantId;

    api
      .listProxyVaultConnections(query)
      .then((res) => {
        if (cancelled) return;
        setConnections(res.data);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load connections');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [api, filters, nonce]);

  return { connections, status, error, filters, setFilters, reload: () => setNonce((n) => n + 1) };
}

export function useProxyVaultConnection(id: string | undefined): {
  connection: ProxyVaultConnection | null;
  status: LoadStatus;
  error: string | null;
  reload: () => void;
  setConnection: (c: ProxyVaultConnection) => void;
} {
  const api = useApiClient();
  const [connection, setConnection] = useState<ProxyVaultConnection | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setStatus('loading');
    api
      .getProxyVaultConnection(id)
      .then((data) => {
        if (cancelled) return;
        setConnection(data);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load connection');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [api, id, nonce]);

  return { connection, status, error, reload: () => setNonce((n) => n + 1), setConnection };
}

export function useProxyVaultValues(
  id: string | undefined,
  tenantId?: string,
): { values: ProxyVaultLedgerEntry[]; status: LoadStatus; reload: () => void } {
  const api = useApiClient();
  const [values, setValues] = useState<ProxyVaultLedgerEntry[]>([]);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setStatus('loading');
    api
      .listProxyVaultValues(id, tenantId)
      .then((res) => {
        if (cancelled) return;
        setValues(res.data);
        setStatus('success');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [api, id, tenantId, nonce]);

  return { values, status, reload: () => setNonce((n) => n + 1) };
}

export function useProxyVaultRevisions(id: string | undefined): {
  revisions: ProxyVaultRevision[];
  reload: () => void;
} {
  const api = useApiClient();
  const [revisions, setRevisions] = useState<ProxyVaultRevision[]>([]);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api
      .listProxyVaultRevisions(id)
      .then((res) => {
        if (!cancelled) setRevisions(res.data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, id, nonce]);

  return { revisions, reload: () => setNonce((n) => n + 1) };
}

/**
 * The live draft-vs-applied preview.
 *
 * Debounced, because it runs on every keystroke and each call re-validates the
 * document against the values and claims ledgers. 400 ms is long enough that
 * typing a host name does not fire six times and short enough that the diff
 * feels attached to the editor.
 */
export function useSpecPreview(
  id: string | undefined,
  draft: string,
  enabled: boolean,
): { preview: ProxyVaultPreview | null; pending: boolean } {
  const api = useApiClient();
  const [preview, setPreview] = useState<ProxyVaultPreview | null>(null);
  const [pending, setPending] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!id || !enabled) return;
    setPending(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api
        .previewProxyVaultSpec(id, draft)
        .then((data) => {
          setPreview(data);
          setPending(false);
        })
        .catch(() => setPending(false));
    }, 400);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [api, id, draft, enabled]);

  return { preview, pending };
}

export interface TrafficFilters {
  host: string;
  kind: 'all' | 'http' | 'binary' | 'tcp';
  connection: 'all' | 'matched' | 'passthrough';
  verdict: 'all' | 'forwarded' | 'refused';
  sinceHours: number;
}

export const DEFAULT_TRAFFIC_FILTERS: TrafficFilters = {
  host: '',
  kind: 'all',
  connection: 'all',
  verdict: 'all',
  sinceHours: 24,
};

/**
 * The traffic ledger, with an optional live tail.
 *
 * Polling rather than a stream: the ledger is written by a fire-and-forget sink
 * on the sidecar, so there is no moment the server could push from, and a
 * three-second poll is indistinguishable from live for a human reading rows.
 */
export function useProxyVaultTraffic(
  filters: TrafficFilters,
  opts: { connectionId?: string | undefined; live?: boolean } = {},
): { exchanges: ProxyVaultExchange[]; status: LoadStatus; reload: () => void } {
  const api = useApiClient();
  const [exchanges, setExchanges] = useState<ProxyVaultExchange[]>([]);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [nonce, setNonce] = useState(0);
  const { connectionId, live } = opts;

  const load = useCallback(async () => {
    const query: Record<string, string> = { since_hours: String(filters.sinceHours), limit: '100' };
    if (filters.host) query['host'] = filters.host;
    if (filters.kind !== 'all') query['kind'] = filters.kind;
    if (filters.verdict !== 'all') query['verdict'] = filters.verdict;
    if (filters.connection !== 'all') query['connection'] = filters.connection;
    const res = connectionId
      ? await api.listProxyVaultConnectionTraffic(connectionId, query)
      : await api.listProxyVaultTraffic(query);
    return res.data;
  }, [api, connectionId, filters]);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    load()
      .then((rows) => {
        if (cancelled) return;
        setExchanges(rows);
        setStatus('success');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [load, nonce]);

  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => {
      load()
        .then(setExchanges)
        .catch(() => undefined);
    }, 3000);
    return () => clearInterval(timer);
  }, [live, load]);

  return { exchanges, status, reload: () => setNonce((n) => n + 1) };
}

export function useProxyVaultExchange(id: string | null): ProxyVaultExchangeDetail | null {
  const api = useApiClient();
  const [exchange, setExchange] = useState<ProxyVaultExchangeDetail | null>(null);

  useEffect(() => {
    if (!id) {
      setExchange(null);
      return;
    }
    let cancelled = false;
    api
      .getProxyVaultExchange(id)
      .then((data) => {
        if (!cancelled) setExchange(data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  return exchange;
}
